import React, { useState, useEffect } from 'react';
import { X, Upload, Palette, Type, Layout, Settings as SettingsIcon, Save } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { GiftCardSettings, GiftCardSettingsService, defaultGiftCardSettings } from '../services/giftCardSettingsService';
import GiftCardWrapper from './GiftCardWrapper';
import { toast } from 'sonner';

interface GiftCardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingDesign?: GiftCardSettings | null;
  onSaveSuccess?: () => void;
}

export default function GiftCardSettingsModal({ isOpen, onClose, editingDesign, onSaveSuccess }: GiftCardSettingsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'design' | 'content'>('design');
  
  const [settings, setSettings] = useState<Partial<GiftCardSettings>>({
    ...defaultGiftCardSettings
  });

  useEffect(() => {
    if (isOpen) {
      // Reset to design tab
      setActiveTab('design');
      
      if (editingDesign) {
        // Editing existing design - load its settings
        console.log('📝 Editing existing design:', editingDesign);
        setSettings({ ...editingDesign });
      } else {
        // Creating new design - use fresh defaults
        console.log('✨ Creating new design with defaults');
        setSettings({ ...defaultGiftCardSettings });
      }
    }
  }, [isOpen, editingDesign, selectedBusiness?.id]);

  const handleSave = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setSaving(true);
      const success = await GiftCardSettingsService.saveGiftCardSettings(
        selectedBusiness.id,
        settings
      );

      if (success) {
        toast.success('Gift card design saved successfully');
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        onClose();
      } else {
        toast.error('Failed to save gift card design');
      }
    } catch (error) {
      console.error('Error saving gift card settings:', error);
      toast.error('Failed to save gift card design');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof GiftCardSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'design' as const, label: 'Design', icon: Palette },
    { id: 'content' as const, label: 'Content', icon: Type },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999] p-3 sm:p-4" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Layout className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editingDesign ? 'Edit Gift Card Design' : 'Create Gift Card Design'}
              </h2>
              <p className="text-sm text-gray-600">Customize the look and feel of your gift cards</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            
            {/* Settings Panel */}
            <div className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  {/* Design Tab */}
                  {activeTab === 'design' && (
                    <div className="space-y-6">
                      {/* Colors Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Colors</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Primary Color
                            </label>
                            <div className="flex items-center space-x-3">
                              <input
                                type="color"
                                value={settings.primary_color}
                                onChange={(e) => updateSetting('primary_color', e.target.value)}
                                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={settings.primary_color}
                                onChange={(e) => updateSetting('primary_color', e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Secondary Color
                            </label>
                            <div className="flex items-center space-x-3">
                              <input
                                type="color"
                                value={settings.secondary_color}
                                onChange={(e) => updateSetting('secondary_color', e.target.value)}
                                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={settings.secondary_color}
                                onChange={(e) => updateSetting('secondary_color', e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Background Color
                            </label>
                            <div className="flex items-center space-x-3">
                              <input
                                type="color"
                                value={settings.background_color}
                                onChange={(e) => updateSetting('background_color', e.target.value)}
                                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={settings.background_color}
                                onChange={(e) => updateSetting('background_color', e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Accent Color
                            </label>
                            <div className="flex items-center space-x-3">
                              <input
                                type="color"
                                value={settings.accent_color}
                                onChange={(e) => updateSetting('accent_color', e.target.value)}
                                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={settings.accent_color}
                                onChange={(e) => updateSetting('accent_color', e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Text Color
                            </label>
                            <div className="flex items-center space-x-3">
                              <input
                                type="color"
                                value={settings.text_color}
                                onChange={(e) => updateSetting('text_color', e.target.value)}
                                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={settings.text_color}
                                onChange={(e) => updateSetting('text_color', e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Style Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Card Style</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Design Style
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {['classic', 'modern', 'elegant', 'playful'].map((style) => (
                                <button
                                  key={style}
                                  onClick={() => updateSetting('card_style', style)}
                                  className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                                    settings.card_style === style
                                      ? 'border-blue-600 bg-blue-50 text-blue-600'
                                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                  }`}
                                >
                                  {style.charAt(0).toUpperCase() + style.slice(1)}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Border Radius
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                              {['sharp', 'rounded', 'pill'].map((radius) => (
                                <button
                                  key={radius}
                                  onClick={() => updateSetting('border_radius', radius)}
                                  className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                                    settings.border_radius === radius
                                      ? 'border-blue-600 bg-blue-50 text-blue-600'
                                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                  }`}
                                >
                                  {radius.charAt(0).toUpperCase() + radius.slice(1)}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Font Family
                            </label>
                            <select
                              value={settings.font_family}
                              onChange={(e) => updateSetting('font_family', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="inter">Inter (Modern)</option>
                              <option value="roboto">Roboto (Clean)</option>
                              <option value="playfair">Playfair (Elegant)</option>
                              <option value="poppins">Poppins (Friendly)</option>
                              <option value="montserrat">Montserrat (Professional)</option>
                            </select>
                          </div>

                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">
                              Show Decorative Elements
                            </label>
                            <button
                              onClick={() => updateSetting('show_decorative_elements', !settings.show_decorative_elements)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                settings.show_decorative_elements ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  settings.show_decorative_elements ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Logo Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Logo</h3>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <label className="text-sm font-medium text-gray-700">
                                Include Business Logo
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Uses your business logo from settings
                              </p>
                            </div>
                            <button
                              onClick={() => updateSetting('include_logo', !settings.include_logo)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                settings.include_logo ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  settings.include_logo ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>

                          {settings.include_logo && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Logo Size
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    onClick={() => updateSetting('logo_size', 'small')}
                                    className={`px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                      settings.logo_size === 'small'
                                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    Small
                                  </button>
                                  <button
                                    onClick={() => updateSetting('logo_size', 'medium')}
                                    className={`px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                      settings.logo_size === 'medium'
                                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                    }`}
                                  >
                                    Medium
                                  </button>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Logo Position
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                  {([
                                    'top-left', 'top-center', 'top-right',
                                    'center-left', 'center', 'center-right',
                                    'bottom-left', 'bottom-center', 'bottom-right'
                                  ] as const).map((position) => (
                                    <button
                                      key={position}
                                      onClick={() => updateSetting('logo_position', position)}
                                      className={`px-2 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                        settings.logo_position === position
                                          ? 'border-blue-600 bg-blue-50 text-blue-600'
                                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                      }`}
                                    >
                                      {position.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Max Height (px)
                                </label>
                                <input
                                  type="range"
                                  min="24"
                                  max="120"
                                  step="4"
                                  value={settings.logo_max_height}
                                  onChange={(e) => updateSetting('logo_max_height', parseInt(e.target.value))}
                                  className="w-full"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                  <span>24px</span>
                                  <span className="font-medium text-gray-700">{settings.logo_max_height}px</span>
                                  <span>120px</span>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Content Tab */}
                  {activeTab === 'content' && (
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Card Title</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Title Text
                            </label>
                            <input
                              type="text"
                              value={settings.card_title}
                              onChange={(e) => updateSetting('card_title', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Title Position
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                onClick={() => updateSetting('card_title_position', 'left')}
                                className={`px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                  settings.card_title_position === 'left'
                                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                Left
                              </button>
                              <button
                                onClick={() => updateSetting('card_title_position', 'center')}
                                className={`px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                  settings.card_title_position === 'center'
                                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                Center
                              </button>
                              <button
                                onClick={() => updateSetting('card_title_position', 'right')}
                                className={`px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                  settings.card_title_position === 'right'
                                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                Right
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Greeting Message</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Message Text
                            </label>
                            <textarea
                              value={settings.greeting_message}
                              onChange={(e) => updateSetting('greeting_message', e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Message Position
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                onClick={() => updateSetting('greeting_message_position', 'left')}
                                className={`px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                  settings.greeting_message_position === 'left'
                                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                Left
                              </button>
                              <button
                                onClick={() => updateSetting('greeting_message_position', 'center')}
                                className={`px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                  settings.greeting_message_position === 'center'
                                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                Center
                              </button>
                              <button
                                onClick={() => updateSetting('greeting_message_position', 'right')}
                                className={`px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${
                                  settings.greeting_message_position === 'right'
                                    ? 'border-blue-600 bg-blue-50 text-blue-600'
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                                }`}
                              >
                                Right
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Live Preview Panel */}
            <div className="lg:sticky lg:top-6 h-fit">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>
                <div className="bg-gray-200 rounded-lg p-8 flex justify-center">
                  <GiftCardWrapper 
                    settings={settings}
                    businessLogoUrl={selectedBusiness?.booking_settings?.logo_url}
                    showSampleData={true}
                    maxWidth="100%"
                  />
                </div>
                <p className="text-xs text-gray-500 text-center mt-4">
                  This is how your gift cards will appear
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>{editingDesign ? 'Update Design' : 'Save Design'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

